import { bareHandle, creatorKey } from '@discovr/features/discovery';
import type { DiscoveryCampaignDetail, DiscoveryPick } from '@discovr/types';
import { NO_VALUE } from './constants/discoveryCopy';
import { matchesCreator, type PickMap } from './results/resultsModel';

export type PoolCreator = DiscoveryCampaignDetail['pool'][number];
export type PoolTab = 'shortlisted' | 'all';
export type PoolStatus = 'shortlisted' | 'rejected' | 'found';

export const POOL_TABS: readonly { tab: PoolTab; label: string }[] = [
  { tab: 'shortlisted', label: 'Shortlisted' },
  { tab: 'all', label: 'All' },
];

export const nameOf = (creator: PoolCreator) =>
  creator.name ?? creator.handle ?? creator.platformId;
export const handleOf = (creator: PoolCreator) => bareHandle(creator.handle ?? creator.platformId);

/** Outreach imports Instagram handles only; a row without a handle has only a platform id, which isn't one. */
export const outreachHandles = (creators: readonly PoolCreator[]): string[] =>
  creators.flatMap((creator) =>
    creator.platform === 'instagram' && creator.handle ? [bareHandle(creator.handle)] : [],
  );

/** The service's own count, as the result card shows it; runs from before it was kept fall back to the catalog states. */
export function checksPassed(creator: PoolCreator): string {
  if (creator.checksAsked) return `${creator.checksPassed ?? 0}/${creator.checksAsked}`;
  const asked = Object.values(creator.vetting).filter((state) => state !== 'not_asked');
  if (!asked.length) return NO_VALUE;
  return `${asked.filter((state) => state === 'pass').length}/${asked.length}`;
}

export function poolStatus(creator: PoolCreator, pick: DiscoveryPick | undefined): PoolStatus {
  if (pick?.shortlisted) return 'shortlisted';
  const restored = pick?.placed === 'passed';
  if (pick?.placed === 'rejected' || (creator.decision === 'rejected' && !restored))
    return 'rejected';
  return 'found';
}

export interface PoolLists {
  all: PoolCreator[];
  shortlisted: PoolCreator[];
  rows: PoolCreator[];
}

export function poolLists(
  pool: readonly PoolCreator[],
  picks: PickMap,
  tab: PoolTab,
  query: string,
): PoolLists {
  const all = [...pool].sort((a, b) => (b.fit ?? 0) - (a.fit ?? 0));
  const shortlisted = all.filter((creator) => picks.get(creatorKey(creator))?.shortlisted);
  const rows = (tab === 'shortlisted' ? shortlisted : all).filter((creator) =>
    matchesCreator(creator, query),
  );
  return { all, shortlisted, rows };
}
